import CancelOrderService from '@/services/isomorphic/CancelOrderService';
import { makeAutoObservable } from 'mobx';
import type { ILineStatus } from '@/services/serverless/integrations/DOMS/DOMSOrderService/types';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import LazyValue from '@/utils/LazyValue';
import { isNullish } from '@/utils/null-utils';
import { OrderNotFoundError } from '@/services/isomorphic/OrderLookupService/errors/OrderNotFoundError';
import type { HistoricOrder } from '@/services/isomorphic/OrderLookupService/data-structures';
import { InvalidStateError } from '@/utils/errors';
import { OrderStatus } from '@/services/models/Order';
import OrderLookupService from '@/services/isomorphic/OrderLookupService';
import type {
  CancellationStatus,
  HistoricOrderShipments,
  IHistoricOrderVM,
  OrderOrID
} from './types';

/** @inheritdoc */
class HistoricOrderVM implements IHistoricOrderVM {
  // #region VM attributes
  /** @inheritdoc */
  public isCancellable: boolean = false;
  /** @inheritdoc */
  public revalidateAfter?: number;
  /** @inheritdoc */
  public cancellationStatus?: CancellationStatus;

  /**
   * The lazy order configuration. Used this way to prevent
   * runtime errors during the construction of the class inside of
   * any provider.
   */
  private _orderConfig = new LazyValue(() =>
    ConfigurationService.getConfig('order')
  );

  /** @inheritdoc */
  public get isInitialized(): boolean {
    return !isNullish(this._orderOrID);
  }

  /**
   * The timeout for getting the order cancellation status.
   * @returns - The timeout as a `number`.
   */
  private get _revalidationTimeout(): number {
    return this._orderConfig.value.getSetting('orderCancelStatusTimeout').value;
  }

  /** The accumulative of revalidation periods. */
  private _totalRevalidation: number = 0;

  /** @inheritdoc */
  public get shipments(): HistoricOrderShipments | undefined {
    if (isNullish(this._orderOrID)) return undefined;
    if (typeof this._orderOrID === 'string') return undefined;
    return this.cancellationStatus !== 'success'
      ? this._orderOrID.shipments
      : this._orderOrID.shipments.map((shipment) => ({
          ...shipment,
          status: OrderStatus.Cancelled
        }));
  }

  /**
   * The provided order or it's ID to use for the VM calculations.
   */
  private _orderOrID?: string | HistoricOrder;

  /** @inheritdoc */
  private get _orderID(): string {
    if (isNullish(this._orderOrID))
      throw new InvalidStateError("There was no order or it's ID initialized");
    return typeof this._orderOrID === 'string'
      ? this._orderOrID
      : this._orderOrID.orderID;
  }
  // #endregion

  // #region Cancel status related methods
  /** Update the revalidation time to the initial value or double it. */
  private _updateRevalidationTimes(): void {
    this._totalRevalidation += this.revalidateAfter ?? 0;
    // If the total amount of revalidation times exceed the configured timeout,
    // update the time to revalidate after to undefined so the caller stops
    // calling the status endpoint.
    if (this._totalRevalidation > this._revalidationTimeout) {
      this.revalidateAfter = undefined;
    } else {
      this.revalidateAfter = isNullish(this.revalidateAfter)
        ? this._orderConfig.value.getSetting(
            'initialRevalidationTimeForOrderCancelStatus'
          ).value
        : this.revalidateAfter * 2;
    }
  }

  /**
   * Update the {@link IHistoricOrderVM.isCancellable isCancellable}
   * and {@link IHistoricOrderVM.revalidateAfter revalidation time} of the
   * visual model depending on the status of the line items.
   * @param lineStatuses - The individual line item {@link ILineStatus status}.
   */
  private _updateCancellationStatusFromLineStatuses(
    lineStatuses: Array<ILineStatus>
  ): void {
    /** An order is only cancellable if all of its line times are. */
    this.isCancellable = lineStatuses.every(({ canCancel }) => canCancel);

    /*
     * The client should only revalidate the status again if the order is not
     * yet cancellable and if none of the line items has been shipped, accepted
     * or already cancelled.
     */
    const noShippedOrCancelledItem = isNullish(
      lineStatuses.find(({ status }) =>
        ['shipped', 'cancelled', 'accepted'].includes(status)
      )
    );
    const shouldRevalidateAgain =
      !this.isCancellable && noShippedOrCancelledItem;
    if (shouldRevalidateAgain) {
      this._updateRevalidationTimes();
    } else {
      this.revalidateAfter = undefined;
    }
  }

  /** @inheritdoc  */
  public async updateCancellationStatus(): Promise<void> {
    try {
      const { lineStatuses } = await CancelOrderService.getStatus(
        this._orderID
      );
      this._updateCancellationStatusFromLineStatuses(lineStatuses);
    } catch (error) {
      this.isCancellable = false;
      if (error instanceof OrderNotFoundError) {
        this.revalidateAfter = undefined;
      } else {
        this._updateRevalidationTimes();
      }
    }
  }
  // #endregion

  // #region Place order cancellation related methods
  /** @inheritdoc */
  public async placeCancel(): Promise<void> {
    this.cancellationStatus = 'loading';
    try {
      await CancelOrderService.place(this._orderID);
      // The order is not cancellable anymore after the request
      // above succeeds.
      this.isCancellable = false;
      this.revalidateAfter = undefined;
      this.cancellationStatus = 'success';
    } catch (error) {
      this.cancellationStatus = 'failure';
    }
  }
  // #endregion

  // #region Initialize related methods.
  /**
   * Check if the VM should revalidate the cancellation status under the following
   * conditions:
   * 1. The VM has not been initialized already.
   * 2. The provided order ID is different from the one already provided.
   * @param orderOrID - The {@link OrderOrID order or it's ID}.
   * @returns - `true` if the VM should revalidate the cancellation status.
   */
  private _shouldRevalidateStatus(orderOrID: OrderOrID): boolean {
    const wasAlreadyInitialized = this.isInitialized;
    if (!wasAlreadyInitialized) return true;

    const orderID =
      typeof orderOrID !== 'string' ? orderOrID.orderID : orderOrID;
    return this._orderID !== orderID;
  }

  /**
   * Check if the provided order could be used to determine if it's cancellable or not.
   * If it could be determined, it will update the {@link IHistoricOrderVM.isCancellable}
   * property of the VM.
   * @param order - The {@link HistoricOrder order}.
   * @returns `true` if the order is indeed returnable, and `undefined` if there's no way
   * to determine if it's cancellable or not.
   */
  private _initializeWithOrder(order: HistoricOrder): boolean | undefined {
    const isOrderCancellable = OrderLookupService.isOrderCancellable(order);
    if (!isNullish(isOrderCancellable)) {
      this.isCancellable = isOrderCancellable;
    }
    return isOrderCancellable;
  }

  /** @inheritdoc */
  public async initialize(orderOrID: OrderOrID): Promise<void> {
    // This is the 1st thing to execute to obtain the correct values.
    const shouldRevalidateStatus = this._shouldRevalidateStatus(orderOrID);
    // We should always update the internal value regarless of the value above.
    this._orderOrID = orderOrID;
    if (!shouldRevalidateStatus) return;

    this._totalRevalidation = 0;
    this.revalidateAfter = undefined;
    this.cancellationStatus = undefined;

    /**
     * Only update the cancellation status with the API if this method
     * received an order ID or if we couldn't determine if the order
     * is cancellable.
     * */
    const shouldUpdateStatusWithAPI =
      typeof orderOrID === 'string'
        ? true
        : (this._initializeWithOrder(orderOrID) ?? true);
    if (shouldUpdateStatusWithAPI) {
      await this.updateCancellationStatus();
    }
  }
  // #endregion

  /** @inheritdoc */
  public constructor() {
    makeAutoObservable(this);
  }
}

export default HistoricOrderVM;
